import React, { useState } from 'react';
import {
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Circle,
  ShoppingCart,
  Zap,
  FileText,
  Trophy,
  Cpu,
  Radar,
  BatteryCharging,
  Monitor,
  Radio,
  Cog,
  Wrench,
  Package,
} from 'lucide-react';
import { STORE_MAP } from '../data/stores';
import './ComponentCard.css';

const priorityConfig = {
  essential: { label: 'Essential', color: '#ff4757', bg: 'rgba(255,71,87,0.1)' },
  recommended: { label: 'Recommended', color: '#ff9900', bg: 'rgba(255,153,0,0.1)' },
  optional: { label: 'Optional', color: '#6b6b88', bg: 'rgba(107,107,136,0.1)' },
};

const categoryIcons = {
  Microcontroller: Cpu,
  Sensor: Radar,
  Power: BatteryCharging,
  Display: Monitor,
  Communication: Radio,
  Actuator: Cog,
  Passive: Wrench,
  Tool: Wrench,
  Other: Package,
};

function parseStorePrice(store) {
  if (Number.isFinite(store?.priceValue)) {
    return store.priceValue;
  }
  const match = String(store?.estimatedPrice || '').match(/([0-9]{2,6}(?:,[0-9]{3})*(?:\.[0-9]{1,2})?)/);
  return match ? Number(String(match[1]).replace(/,/g, '')) : Number.POSITIVE_INFINITY;
}

export default function ComponentCard({
  component,
  selected,
  onToggle,
  selectedStoreId,
  onSelectStore,
  index,
}) {
  const [expanded, setExpanded] = useState(false);
  const pConfig = priorityConfig[component.priority] || priorityConfig.optional;
  const CategoryIcon = categoryIcons[component.category] || Package;
  const stores = component.stores || [];
  const sortedStores = [...stores].sort((a, b) => parseStorePrice(a) - parseStorePrice(b));
  const bestStore = sortedStores[0] || stores[0];
  const bestStoreData = bestStore ? STORE_MAP[bestStore.storeId] : null;
  const activeStoreId = selectedStoreId || bestStore?.storeId || null;

  return (
    <div
      className={`component-card ${selected ? 'selected' : ''}`}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="card-main">
        <button className="card-select" onClick={onToggle}>
          {selected ? <CheckCircle2 size={20} className="check-icon checked" /> : <Circle size={20} className="check-icon" />}
        </button>

        <div className="card-icon">
          <CategoryIcon size={17} />
        </div>

        <div className="card-info">
          <div className="card-top">
            <h3 className="card-name">{component.name}</h3>
            <span className="priority-tag" style={{ color: pConfig.color, background: pConfig.bg }}>
              {pConfig.label}
            </span>
            {bestStoreData && (
              <span className="lowest-badge" title="Lowest estimated store price">
                <Trophy size={11} /> Lowest: {bestStoreData.name}
              </span>
            )}
          </div>
          <p className="card-desc">{component.description}</p>
          <div className="card-meta">
            <span className="mono card-qty">Qty: {component.quantity}</span>
            <span className="card-dot">.</span>
            <span className="card-category">{component.category}</span>
            {component.specifications && (
              <>
                <span className="card-dot">.</span>
                <span className="card-specs">{component.specifications}</span>
              </>
            )}
          </div>
        </div>

        <div className="card-right">
          <div className="card-price">{component.estimatedPrice}</div>
          {bestStoreData && (
            <div className="best-store" style={{ color: bestStoreData.color }}>
              {bestStoreData.logo} {bestStoreData.name}
            </div>
          )}
          <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
            <span className="mono">{stores.length} stores</span>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {component.datasheetUrl && (
            <a className="datasheet-btn mono" href={component.datasheetUrl} target="_blank" rel="noreferrer">
              <FileText size={11} /> Datasheet
            </a>
          )}
        </div>
      </div>

      {expanded && (
        <div className="store-list animate-in">
          <div className="store-list-header">
            <span className="mono">Price compare across {stores.length} stores</span>
          </div>
          <div className="stores-grid">
            {sortedStores.map((store, i) => {
              const storeData = STORE_MAP[store.storeId];
              if (!storeData) return null;
              const searchUrl = storeData.searchUrl + encodeURIComponent(store.searchQuery);
              const avail = store.availability;
              return (
                <div
                  key={i}
                  className="store-item"
                  style={{ '--store-color': storeData.color }}
                >
                  <div className="store-item-left">
                    <span className="store-logo">{storeData.logo}</span>
                    <div>
                      <div className="store-name">{storeData.name}</div>
                      <div className="store-tag mono">{storeData.tag}</div>
                    </div>
                  </div>
                  <div className="store-item-right">
                    <span className="store-price">{store.estimatedPrice}</span>
                    <span className={`avail-dot avail-${avail}`} title={`${avail} availability`} />
                    <div className="store-actions">
                      {selected && (
                        <button
                          className={`pick-store-btn ${activeStoreId === store.storeId ? 'active' : ''}`}
                          onClick={() => onSelectStore(store.storeId)}
                        >
                          {activeStoreId === store.storeId ? 'Selected' : 'Use in Cart'}
                        </button>
                      )}
                      <a href={searchUrl} target="_blank" rel="noreferrer" className="store-link-icon" aria-label={`Open ${storeData.name}`}>
                        <ExternalLink size={12} className="store-ext" />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {component.notes && (
            <div className="component-note">
              <Zap size={12} />
              <span>{component.notes}</span>
            </div>
          )}
        </div>
      )}

      {selected && !expanded && (
        <div className="quick-buy">
          {sortedStores.slice(0, 3).map((store, i) => {
            const storeData = STORE_MAP[store.storeId];
            if (!storeData) return null;
            return (
              <a
                key={i}
                href={storeData.searchUrl + encodeURIComponent(store.searchQuery)}
                target="_blank"
                rel="noreferrer"
                className="quick-buy-btn"
                style={{ borderColor: storeData.color + '44', color: storeData.color }}
              >
                <ShoppingCart size={11} />
                {storeData.name}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
